"use client";

import { ShieldCheck, Scale, Handshake, TrendingUp } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Advocate() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const features = [{
    number: "01",
    title: "EXPERT NEGOTIATION",
    description: "We leverage deep market insights to secure the best possible terms for your high-value purchases, ensuring you never overpay.",
    Icon: Scale
  }, {
    number: "02",
    title: "TOTAL TRANSPARENCY",
    description: "Our process is built on clear communication and honest advice, keeping you informed at every stage of the transaction.",
    Icon: ShieldCheck
  }, {
    number: "03",
    title: "PERSONALIZED SUPPORT",
    description: "We act as your dedicated partner, providing tailored strategies that align with your specific financial goals and needs.",
    Icon: Handshake
  }, {
    number: "04",
    title: "MAXIMIZED SAVINGS",
    description: "Our proven methodology focuses on identifying hidden value and reducing costs, putting more money back into your pocket.",
    Icon: TrendingUp
  }];
  return <section data-section-id="4118" id="why-choose-an-advocate" className="bg-background text-foreground">
      <div className="bg-white text-gray-900 py-24 md:py-32 lg:py-40 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {}
          <div ref={headerRef} className={`flex flex-col items-center text-center transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="font-default text-xs md:text-sm tracking-[0.2em] text-orange-600 relative inline-block pb-2 uppercase font-bold">
              <span>Why Choose Price Advocate?</span>
              <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-blue-900" />
            </div>

            {}
            <h2 className="font-default font-bold uppercase mt-12 md:mt-16 leading-[0.95] tracking-tighter text-blue-900" style={{
            fontSize: "clamp(2.5rem, 9vw, 6rem)"
          }}>
              <span className="block">YOUR SHIELD IN</span>
              <span className="block">EVERY HIGH-STAKES</span>
              <span className="block text-orange-600">PURCHASE.</span>
            </h2>
          </div>

          {}
          <div className="mt-20 md:mt-28 lg:mt-32 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16 lg:gap-20">
            {features.map((feature, i) => {
            const Icon = feature.Icon;
            return <FeatureItem key={i} data-index={i} number={feature.number} title={feature.title} description={feature.description} Icon={Icon} index={i} />;
          })}
          </div>

          {}
          <div ref={footerRef} className={`mt-24 md:mt-32 lg:mt-40 text-center transition-all duration-700 ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default italic text-gray-900 leading-tight" style={{
            fontSize: "clamp(1.25rem, 2.5vw, 2rem)"
          }}>
              We don't just negotiate prices; we advocate for your financial peace of mind
              <span className="text-orange-600">.</span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function FeatureItem({
  number,
  title,
  description,
  Icon,
  index,
  ...rest
}: {
  number: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} {...rest} className={`group flex flex-col transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      {}
      <div className="font-default font-bold text-blue-900 leading-none transition-transform duration-300 group-hover:scale-110 origin-left" style={{
      fontSize: "clamp(2.5rem, 4vw, 4rem)"
    }}>
        {number}
      </div>

      {}
      <div className="relative mt-6 mb-6 h-[1.5px] w-full bg-gray-300 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-0 bg-orange-600 transition-all duration-500 group-hover:w-full" />
      </div>

      {}
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-default font-bold text-blue-900 text-xl md:text-2xl uppercase leading-tight tracking-tight">
          {title}
        </h3>
        <Icon className="size-7 md:size-8 text-blue-900 shrink-0 transition-colors duration-300 group-hover:text-orange-600" strokeWidth={1.5} />
      </div>

      {}
      <p className="font-default text-base md:text-lg leading-relaxed text-gray-700 mt-5">
        {description}
      </p>
    </div>;
}